import { useEffect, useState } from 'react';
import { useRegionalConfiguration } from '@tn/foundation/entities/regional-configuration/hooks/useRegionalConfiguration';
import useRegion from '@tn/foundation/hooks/providers/useRegion';
import { FLOW_ID_SESSION_KEY } from '@tn/foundation/utils/constants/common';
import { useRouter } from 'next/router';

import { getProtectedFlowIdForPath } from './helpers';

type UseFlowRouteGuardResult = {
    isBlocked: boolean;
};

/**
 * Central route guard for flow-only pages.
 *
 * For each navigation, checks whether the current pathname is "protected-only" for some flow —
 * i.e. it's declared in that flow's `relevantPaths` exclusively via `protected: true` entries.
 * Such paths require the flow to already be active (sessionStorage `FLOW_ID_SESSION_KEY` already
 * holds the flow id); otherwise the user is redirected to `/`.
 *
 * Mounted once in `StateMachineWrapper`, so individual page components stay unaware of the gate.
 */
const useFlowRouteGuard = (): UseFlowRouteGuardResult => {
    const router = useRouter();
    const { region } = useRegion();
    const { getConfigByKey } = useRegionalConfiguration({ appName: 'signup', region });
    const [isBlocked, setIsBlocked] = useState(false);

    useEffect(() => {
        const protectedFlowId = getProtectedFlowIdForPath(router.pathname, getConfigByKey, region);
        if (!protectedFlowId) {
            setIsBlocked(false);
            return;
        }
        const sessionFlowId = sessionStorage.getItem(FLOW_ID_SESSION_KEY);
        if (sessionFlowId === protectedFlowId) {
            setIsBlocked(false);
            return;
        }
        setIsBlocked(true);
        router.replace('/');
    }, [router, router.pathname, getConfigByKey, region]);

    return { isBlocked };
};

export default useFlowRouteGuard;
